import { stringify } from 'querystring';
import { reduce, complement, equals } from 'ramda';
import { useEffect, useRef } from 'react';
import { history } from '@peloton/redux';
import type { AppliedFilter, SortValue } from '@engage/filters';
import {
  getAppliedFilters,
  overrideAppliedFilters,
  getCurrentSort,
  sort as sortAction,
} from '@engage/filters';
import { useReduxState, useReduxAction } from '@engage/redux';
import {
  filtersEqual,
  getCurrentFiltersFromSearch,
  getCurrentSortFromSearch,
} from './Filters/redux';
import { reloadPage } from './Filters/sagas';

const filtersNotEqual = complement(filtersEqual);
const notEquals = complement(equals);

const safelyStringify = (v: unknown) => {
  try {
    return JSON.stringify(v);
  } catch (e) {
    return JSON.stringify([]);
  }
};

export const useSyncStateToUrl = () => {
  const currentStateFilters = useReduxState(getAppliedFilters);
  const filterRef = useRef(currentStateFilters);
  const currentURLFilters = useReduxState(getCurrentFiltersFromSearch);

  const currentStateSort = useReduxState(getCurrentSort);
  const sortRef = useRef(currentStateSort);
  const currentURLSort = useReduxState(getCurrentSortFromSearch);

  const updateURL = () => {
    const filtersAsMap = reduce(
      (acc, elem) => ({
        ...acc,
        [elem.name]: !!acc[elem.name] ? [...acc[elem.name], ...elem.value] : elem.value,
      }),
      {},
      currentStateFilters,
    );

    const encodedMap = Object.entries(filtersAsMap).reduce(
      (acc, [k, v]) => ({
        ...acc,
        [k]: safelyStringify(v),
      }),
      {},
    );

    history.push({
      search: `?${stringify({
        ...encodedMap,
        sort: currentStateSort.sort,
        desc: currentStateSort.desc,
      })}`,
    });
  };

  useEffect(() => {
    syncMyStateToTheirs<AppliedFilter[]>({
      myCurrentState: currentStateFilters,
      myPreviousState: filterRef.current,
      theirCurrentState: currentURLFilters,
      updateTheirState: updateURL,
      setMyPreviousState: filters => (filterRef.current = filters),
      notEqual: filtersNotEqual,
    });
  });

  useEffect(() => {
    syncMyStateToTheirs<SortValue>({
      myCurrentState: currentStateSort,
      myPreviousState: sortRef.current,
      theirCurrentState: currentURLSort,
      updateTheirState: updateURL,
      setMyPreviousState: sort => (sortRef.current = sort),
      notEqual: notEquals,
    });
  });
};

export const useSyncUrlToState = (isLoading: boolean) => {
  const currentURLFilters = useReduxState(getCurrentFiltersFromSearch);
  const stateFilters = useReduxState(getAppliedFilters);
  const filterRef = useRef(currentURLFilters);

  const override = useReduxAction(overrideAppliedFilters);
  const reload = useReduxAction(reloadPage);

  const currentURLSort = useReduxState(getCurrentSortFromSearch);
  const currentStateSort = useReduxState(getCurrentSort);
  const sortRef = useRef(currentURLSort);
  const updateReduxSortState = useReduxAction(sortAction);

  const updateReduxFilterState = () => {
    override(currentURLFilters);
    reload();
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    syncMyStateToTheirs<AppliedFilter[]>({
      myCurrentState: currentURLFilters,
      myPreviousState: filterRef.current,
      theirCurrentState: stateFilters,
      updateTheirState: updateReduxFilterState,
      setMyPreviousState: filters => (filterRef.current = filters),
      notEqual: filtersNotEqual,
    });
  });

  useEffect(() => {
    if (isLoading) {
      return;
    }

    syncMyStateToTheirs<SortValue>({
      myCurrentState: currentURLSort,
      myPreviousState: sortRef.current,
      theirCurrentState: currentStateSort,
      updateTheirState: () => updateReduxSortState(currentURLSort),
      setMyPreviousState: sort => (sortRef.current = sort),
      notEqual: notEquals,
    });
  });
};

export const syncMyStateToTheirs = <T extends any>({
  myCurrentState,
  myPreviousState,
  theirCurrentState,
  updateTheirState,
  setMyPreviousState,
  notEqual,
}: {
  myCurrentState: T;
  myPreviousState: T;
  theirCurrentState: T;
  notEqual(a: T, b: T): boolean;
  updateTheirState(): void;
  setMyPreviousState(f: T): void;
}) => {
  if (
    notEqual(myCurrentState, myPreviousState) && // I have changed
    notEqual(myCurrentState, theirCurrentState) // they don't have the change yet
  ) {
    updateTheirState();
  }
  setMyPreviousState(myCurrentState);
};
